import { useMemo } from "react";
import useConfig from "@/hooks/useConfig";
import { LIVE_CHANNEL_IDS } from "@/v2/lib/liveChannels";
import { useChannels, useInfiniteEvents } from "../Events/api";
import { formatGameTime } from "../Events/date-utils";
import type { Game } from "../Events/types";
import { scopeToChannels } from "./channelFilter";
import type { ContentItem } from "./types";

/** The VOD API has no runtime, so a game fills a fixed 3 hour block on the timeline. */
export const ASSUMED_GAME_DURATION_SEC = 3 * 3600;

const PAGE_SIZE = 24;

function teamName(name?: string, mascot?: string) {
  return `${name ?? ""} ${mascot ?? ""}`.trim();
}

export function gameToContent(game: Game): ContentItem {
  const away = teamName(game.away_team, game.away_team_mascot);
  const home = teamName(game.home_team, game.home_team_mascot);
  return {
    id: `vod-${game.game_id}`,
    title: away && home ? `${away} vs. ${home}` : away || home || `Game ${game.game_id}`,
    type: "game",
    league: game.away_team_desc || game.home_team_desc || "",
    thumbnail: game.card_image_url_wide || game.card_image_url || "",
    logos: [game.away_team_logo, game.home_team_logo].filter((l): l is string => Boolean(l)),
    channelLogo: game.card_logo || "",
    channelId:
      game.broadcast_platform_channel_id != null ? String(game.broadcast_platform_channel_id) : undefined,
    durationSec: ASSUMED_GAME_DURATION_SEC,
    durationEstimated: true,
    date: (game.game_date_time || game.game_date || "").slice(0, 10),
    time: formatGameTime(game.game_date_time) || undefined,
    facility: game.facility_name || undefined,
    // The whole feed record rides along so a scheduled program's metadata
    // snapshots every game detail, not just the card fields.
    source: game as unknown as Record<string, unknown>,
  };
}

/**
 * Games from the same on-demand feed the /vod page lists, optionally
 * narrowed to one or more broadcast channels the same way that page's
 * sidebar does: `channel` is "" for every channel, a comma-separated id
 * list, or NO_CHANNELS_SELECTED (see channelFilter.ts) for none.
 *
 * `search` is sent to the same API `search` param the /vod page uses, rather
 * than filtered only in the browser: the library's title/team/ID search box
 * narrows what has already loaded so aggressively that a client-only filter
 * left the infinite-scroll sentinel visible (too little on screen to fill
 * the scroll area) with hasNextPage still true, so it just kept fetching
 * page after page forever looking for matches. Filtering server-side keeps
 * the loaded and displayed counts in sync, so hasNextPage runs out normally.
 *
 * Only the 24x7 channels (LIVE_CHANNEL_IDS) can be scheduled, so both the
 * dropdown's channel list and the `channel` param are scoped to them: "all
 * channels" asks for those ids, never the whole tenant.
 */
export function useVodGames(channel = "", search = "") {
  const config = useConfig();
  const customer = String(config?.customer ?? "");
  const channelsQuery = useChannels(customer);
  const query = useInfiniteEvents({
    customer,
    status: "vod",
    limit: PAGE_SIZE,
    start: "",
    end: "",
    channel: scopeToChannels(channel, LIVE_CHANNEL_IDS),
    search,
  });
  const channels = useMemo(
    () => (channelsQuery.data ?? []).filter((c) => LIVE_CHANNEL_IDS.includes(Number(c.channel_id))),
    [channelsQuery.data],
  );

  const games = useMemo(() => {
    const seen = new Set<string>();
    const out: ContentItem[] = [];
    for (const game of query.data?.pages.flat() ?? []) {
      if (game?.game_id == null || seen.has(String(game.game_id))) continue;
      seen.add(String(game.game_id));
      out.push(gameToContent(game));
    }
    return out;
  }, [query.data]);

  return {
    games,
    channels,
    isLoading: query.isLoading,
    isError: query.isError,
    hasNextPage: Boolean(query.hasNextPage),
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: () => void query.fetchNextPage(),
  };
}
